import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { services } from '../app';
import { FEEDBACK_TEXT_MAX } from '../../server/feedback';
import { feedbackContext, sendFeedback, type FeedbackKind, type SendOutcome } from '../live/feedback';
import { panel, showToast } from '../state';
import { Sheet } from './chrome';
import { Icon } from './icons';
import { trail, type Seen } from './trail';

// Tell us (the owner, 24 October 2026, DECISIONS 112): what is not right, or an idea. Opened from the top of Help (the
// question mark is on the sky throughout) and from More. It asks what it is about, offering the last few things looked
// at by name (src/ui/trail.ts), so nobody needs to know what a page is called; then which is closest, and a few words,
// which the phone's keyboard microphone can take down. The details that help look into it go too (src/live/feedback.ts).

export const KINDS: [FeedbackKind, string][] = [
  ['wrong', 'It looks wrong'],
  ['broken', 'It didn’t work'],
  ['hard', 'It’s hard to use'],
  ['idea', 'I have an idea'],
];
export const OTHER: Seen = { key: 'other', label: 'Something else', icon: 'message' };
/** The last few things looked at, then "Something else". */
const CHOICES = 4;

export const THANKS: Record<Exclude<SendOutcome, 'failed'>, string> = {
  sent: 'We read every message.',
  offline: 'You’re offline, so it will be sent when you’re back online.',
  later: 'It will be sent a little later.',
};
export const NOT_SENT = 'Sorry, that could not be sent. Please try again.';
/** When the sheet was closed while it sent. */
export const NOT_SENT_CLOSED = 'Sorry, your message could not be sent. Your words are kept to try again.';

/** Search finds "Send feedback", and Settings leads to Help: a way here is offered after what was looked at. */
const WAYS_IN = new Set(['panel:search', 'panel:settings']);

/** What was chosen and written, kept until it is sent (in memory only), so a sheet closed by mistake loses nothing. */
let draft: { about?: string; kind: FeedbackKind | null; text: string } = { kind: null, text: '' };
export const clearDraft = () => void (draft = { kind: null, text: '' });

export function FeedbackPanel() {
  // As they were when it opened.
  const choices = useMemo(() => {
    const recent = trail.peek().slice(0, CHOICES);
    const [first, ...rest] = recent;
    return [...(first && WAYS_IN.has(first.key) && rest.length ? [...rest, first] : recent), OTHER];
  }, []);
  const [about, setAbout] = useState<Seen | null>(() => choices.find((c) => c.key === draft.about) ?? null);
  const [kind, setKind] = useState<FeedbackKind | null>(draft.kind);
  const [text, setText] = useState(draft.text);
  const [state, setState] = useState<'writing' | 'sending' | SendOutcome>('writing');
  const done = useRef<HTMLButtonElement>(null);
  /** One message per tap, however fast the taps. */
  const busy = useRef(false);
  /** The sheet has closed (while sending, the outcome then shows as a message). */
  const gone = useRef(false);
  useEffect(() => () => void (gone.current = true), []);
  const close = () => (panel.value = null);
  const words = text.trim();
  const ready = !!about && (!!kind || !!words) && state !== 'sending';
  const thanked = state === 'sent' || state === 'offline' || state === 'later';
  useEffect(() => {
    if (thanked) done.current?.focus({ preventScroll: true });
  }, [thanked]);
  const chooseAbout = (c: Seen) => {
    draft.about = c.key;
    setAbout(c);
  };
  const chooseKind = (k: FeedbackKind) => {
    draft.kind = k;
    setKind(k);
  };
  const write = (t: string) => {
    draft.text = t;
    setText(t);
  };
  const send = async () => {
    if (busy.current || !about || !ready) return;
    busy.current = true;
    setState('sending');
    const seen = choices.filter((c) => c !== OTHER).map((c) => c.label);
    const outcome = await sendFeedback({ v: 1, about: { key: about.key, label: about.label }, kind, text: words, context: feedbackContext(services.peek(), seen) });
    busy.current = false;
    if (outcome !== 'failed') clearDraft();
    if (gone.current) showToast(outcome === 'failed' ? NOT_SENT_CLOSED : `Thank you. ${THANKS[outcome]}`, 6000);
    else setState(outcome);
  };
  return (
    <Sheet onClose={close} size="tall" label="Tell us">
      {thanked ? (
        <div class="fb-done" role="status">
          <span class="fb-done-icon" aria-hidden="true"><Icon name="check" size={26} stroke={2.2} /></span>
          <h2 class="fb-done-title">Thank you</h2>
          <p class="sheet-lead">{THANKS[state]}</p>
          <button class="btn" ref={done} onClick={close}>Done</button>
        </div>
      ) : (
        <>
          <h2 class="sheet-title">Tell us</h2>
          <p class="sheet-lead">Something not right, or an idea? We read every message.</p>

          <h3 class="section-title" id="fb-about">What’s it about?</h3>
          {/* Real radio buttons, so arrow keys move between the choices and each group is one stop for Tab. */}
          <div class="results" role="radiogroup" aria-labelledby="fb-about">
            {choices.map((c) => {
              const on = about?.key === c.key;
              return (
                <label key={c.key} class={`result fb-row ${on ? 'on' : ''}`}>
                  <input type="radio" class="fb-radio" name="fb-about" checked={on} onChange={() => chooseAbout(c)} />
                  <span class="result-icon"><Icon name={c.icon} size={20} /></span>
                  <span class="result-text"><span class="result-label">{c.label}</span></span>
                  {on && <Icon name="check" size={18} stroke={2.2} />}
                </label>
              );
            })}
          </div>

          {about && (
            <>
              <h3 class="section-title" id="fb-kind">Which is closest?</h3>
              <div class="band-row wrap fb-kinds" role="radiogroup" aria-labelledby="fb-kind">
                {KINDS.map(([k, label]) => (
                  <label key={k} class={`band-chip fb-kind ${kind === k ? 'on' : ''}`}>
                    <input type="radio" class="fb-radio" name="fb-kind" checked={kind === k} onChange={() => chooseKind(k)} />
                    {label}
                  </label>
                ))}
              </div>

              <h3 class="section-title" id="fb-words">{kind ? 'Anything to add?' : 'What happened?'}</h3>
              <textarea
                class="night-note fb-text"
                aria-labelledby="fb-words"
                aria-describedby="fb-mic"
                rows={4}
                maxLength={FEEDBACK_TEXT_MAX}
                placeholder="A few words are plenty"
                value={text}
                onInput={(e) => write((e.target as HTMLTextAreaElement).value)}
              />
              <p class="fb-mic" id="fb-mic"><Icon name="mic" size={15} /> To speak instead of typing, tap the microphone on your keyboard.</p>

              {state === 'failed' && <p class="fb-error" role="alert">{NOT_SENT}</p>}
              <button class="btn fb-send" disabled={!ready} onClick={() => void send()}>{state === 'sending' ? 'Sending…' : 'Send'}</button>
              <p class="fine">With it go your phone type, Ahillah’s version and settings, what the sky showed, the last few things you looked at, any errors and your location to about 10 km, to help us look into it. Please leave out names and contact details.</p>
            </>
          )}
        </>
      )}
    </Sheet>
  );
}
